import Box from '@material-ui/core/Box'
import { makeStyles } from '@material-ui/core/styles'
import Skeleton from '@material-ui/lab/Skeleton'
import React, {
  lazy,
  Suspense,
  useCallback,
  useContext,
  useMemo,
  useRef,
} from 'react'

import { createSlickArrows } from '../../../../Components/SlickArrows'
import colorContext from '../../../../Context/colorContext'
import useSlickA11y from '../../../../hooks/useSlickA11y'

const Slider = lazy(() => import('react-slick'))

export const SkeletonBanners = (): JSX.Element => {
  const { colors } = useContext(colorContext)
  const useStyles = makeStyles(() => ({
    main: {
      width: '100%',
      display: 'flex',
      justifyContent: 'center',
      backgroundColor: colors.colorBackground,
      alignItems: 'center',
      padding: '50px 5px',
    },
    bannerMain: {
      width: '100%',
      maxWidth: 1320,
      borderRadius: 4,
      maxHeight: 382,
      backgroundColor: 'rgba(247,250,255)',
    },
    textTitleInitial: {
      position: 'relative',
      textTransform: 'uppercase',
      fontFamily: 'Roboto',
      padding: '10px 0px 0px 3px',
      marginBottom: 5,
      fontWeight: 'bold',
      color: colors.textBody,
      left: 8,
      '&::after': {
        content: '""',
        display: 'block',
        width: 4,
        height: 24,
        backgroundColor: colors.textBody,
        position: 'absolute',
        bottom: 6,
        marginLeft: -14,
        left: 5,
      },
    },
    textSubTitle: {
      position: 'relative',
      fontFamily: 'Roboto',
      paddingBottom: 10,
      color: colors.textBody,
      letterSpacing: '0.1px',
      lineHeight: '24px',
      fontWeight: 400,
      fontSize: 18,
      opacity: 0.5,
    },
    boxImage: {
      height: '100%',
      maxHeight: 382,
      width: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    legend: {
      position: 'absolute',
      zIndex: 9999,
      top: '70%',
      left: 0,
      right: 0,
      backgroundColor: 'rgba(11, 11, 11, 0.36)',
      padding: 10,
    },
    textLegend: {
      textTransform: 'capitalize',
      textDecoration: 'none',
      color: colors.textAccentColor,
      fontFamily: 'Roboto',
      fontSize: 16,
      fontWeight: 600,
      '&:hover': {
        textDecoration: 'underline',
      },
    },
  }))
  const classes = useStyles()
  const sliderRef = useRef<HTMLDivElement>(null)
  useSlickA11y(sliderRef)
  const { NextArrow, PrevArrow } = useMemo(
    () =>
      createSlickArrows({
        nextLabel: 'Próximo slide',
        prevLabel: 'Slide anterior',
      }),
    [],
  )

  const settings = {
    nextArrow: <NextArrow />,
    prevArrow: <PrevArrow />,
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
  }

  const renderContent = useCallback(() => {
    return (
      <Suspense fallback="">
        <div
          ref={sliderRef}
          role="region"
          aria-roledescription="carrossel"
          aria-label="Carregando banners"
        >
          <Slider {...settings}>
            {Array.from({ length: 5 }).map((_, index) => (
              <Skeleton
                key={index}
                height={382}
                className={classes.bannerMain}
              />
            ))}
          </Slider>
        </div>
      </Suspense>
    )
  }, [settings, classes])

  return (
    <Box className={classes.main}>
      <Box
        display="flex"
        maxWidth="1320px"
        width="100%"
        flexDirection="column"
        gridGap={30}
      >
        {renderContent()}
      </Box>
    </Box>
  )
}
